<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, type RouteLocationRaw } from 'vue-router'
import type { ChatEmbed } from '@/services/chat/chatHttp.service'
import { useChatStore } from '@/config/store/chatStore'
import ResourceImage from '@/components/resource/resourceImage.vue'
import MaterialSymbolsPersonRounded from '~icons/material-symbols/person-rounded'
import MaterialSymbolsWorkOutline from '~icons/material-symbols/work-outline'
import MaterialSymbolsPersonSearch from '~icons/material-symbols/person-search'
import MaterialSymbolsRateReviewOutline from '~icons/material-symbols/rate-review-outline'
import MaterialSymbolsArrowForwardIosRounded from '~icons/material-symbols/arrow-forward-ios-rounded'
import MaterialSymbolsCloseRounded from '~icons/material-symbols/close-rounded'

const { t } = useI18n()
const chatStore = useChatStore()

const props = defineProps<{
  embed: ChatEmbed
  compact?: boolean
  tone?: 'default' | 'on-primary'
  /** Show a remove control (composer pending embeds) — disables navigation */
  removable?: boolean
}>()

const emits = defineEmits<{
  (e: 'remove'): void
}>()

const typeLabel = computed(() => {
  const map: Record<string, string> = {
    resource: t('modules.chat.embed.resource'),
    process: t('modules.chat.embed.candidate'),
    candidate: t('modules.chat.embed.candidate'),
    project: t('modules.chat.embed.project'),
    evaluation: t('modules.chat.embed.evaluation'),
  }
  return map[props.embed.type] || props.embed.type
})

const icon = computed(() => {
  switch (props.embed.type) {
    case 'resource': return MaterialSymbolsPersonRounded
    case 'project': return MaterialSymbolsWorkOutline
    case 'evaluation': return MaterialSymbolsRateReviewOutline
    default: return MaterialSymbolsPersonSearch
  }
})

const entityId = computed(() => String(props.embed.entityId || ''))

const to = computed<RouteLocationRaw | null>(() => {
  if (props.removable) return null
  const id = entityId.value
  if (!id) return null

  const type = props.embed.type
  if (type === 'resource') {
    return { name: 'EmployeeProfile', params: { resourceId: id } }
  }
  if (type === 'process' || type === 'candidate') {
    return { name: 'RecruitmentProcessInterviews', params: { processId: id } }
  }
  if (type === 'project') {
    return { name: 'ProjectInformationPage', params: { projectId: id } }
  }
  if (type === 'evaluation') {
    return { name: 'Evaluation', params: { evaluationId: id } }
  }
  return null
})

const onNavigate = () => {
  if (!to.value) return
  chatStore.collapseWidget()
}

const onRemove = (event: Event) => {
  event.preventDefault()
  event.stopPropagation()
  emits('remove')
}
</script>

<template>
  <component
    :is="to ? RouterLink : 'div'"
    v-bind="to ? { to } : {}"
    class="w-full text-left mt-2 rounded-xl px-2.5 py-2 flex gap-2 items-center transition-colors"
    data-cy="chat-embed-card"
    :data-embed-type="embed.type"
    :class="[
      tone === 'on-primary'
        ? 'border border-white/25 bg-white/10'
        : 'border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5',
      to
        ? [
            'cursor-pointer active:scale-[0.99]',
            tone === 'on-primary' ? 'hover:bg-white/20' : 'hover:bg-black/10 dark:hover:bg-white/10',
          ]
        : '',
    ]"
    :title="to ? t('modules.chat.embed.open') : undefined"
    @click.stop="onNavigate"
  >
    <ResourceImage
      v-if="embed.snapshot?.picture"
      :resource="{ firstname: embed.snapshot.title?.split(' ')[0], lastname: embed.snapshot.title?.split(' ').slice(1).join(' '), picture: embed.snapshot.picture }"
      width="w-8"
      height="h-8"
      disable-link
    />
    <div
      v-else
      class="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center shrink-0"
      :class="tone === 'on-primary' ? 'bg-white/20' : ''"
    >
      <component
        :is="icon"
        class="w-4 h-4"
      />
    </div>
    <div class="min-w-0 flex-1">
      <div class="text-[10px] uppercase tracking-wide opacity-60">
        {{ typeLabel }}
      </div>
      <div
        class="text-sm font-medium truncate underline-offset-2 group-hover:underline"
        data-cy="chat-embed-title"
      >
        {{ embed.snapshot?.title || '—' }}
      </div>
      <div
        v-if="embed.snapshot?.subtitle"
        class="text-xs opacity-60 truncate"
      >
        {{ embed.snapshot.subtitle }}
      </div>
    </div>
    <button
      v-if="removable"
      type="button"
      class="p-1 rounded-lg bg-hover shrink-0 active:scale-95"
      :title="t('modules.chat.embed.remove')"
      @click="onRemove"
    >
      <MaterialSymbolsCloseRounded class="w-4 h-4" />
    </button>
    <MaterialSymbolsArrowForwardIosRounded
      v-else-if="to"
      class="w-3.5 h-3.5 opacity-50 shrink-0"
    />
  </component>
</template>
